import type { ReactElement, ReactNode } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../../../bev-safe-text';
import Input from '../../../input';
import TextArea from '../../../textarea';
import Select from '../../../select';
import Checkbox from '../../../checkbox';
import ItemAdd from '../../../icon/item_add';
import ItemRemove from '../../../icon/item_remove';
import Alert from '../../../icon/alert';

import { INPUT_TYPES } from './constants';
import type { PriceField } from './types';

export type BookingFormFieldProps = {
	field: PriceField;
	value: unknown;
	errorMessage?: ReactNode;
	onChange: (
		evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
	) => void;
	onBlur: (
		evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
	) => void;
};

/**
 * Field renderer standing in for the legacy `FieldSelector`. Switches on the field's `type` (same
 * domain as the old `INPUT_TYPES`) and matches the Figma "Select Preferences" spec for the types it
 * covers there (DROPDOWN, RADIO/BUTTON_OPTIONS segmented control, NUMBER stepper, TEXTAREA, EMAIL).
 * Anything without a spec (DATE/DATE_PICKER/PHONE/CARD_NUMBER/CARD_EXPIRY_DATE/TIME) falls back to a
 * plain labelled text input.
 */
export default function BookingFormField({
	field,
	value,
	errorMessage,
	onChange,
	onBlur,
}: BookingFormFieldProps): ReactElement {
	const { name, label, required, type, options, min, max, secondaryLabel } = field;

	// One decorated error node (alert icon + message) reused across every field type so validation
	// feedback looks the same everywhere, not just on the email field.
	const decoratedError: ReactNode = errorMessage ? (
		<span className="flex items-start gap-040">
			<Alert width={14} height={14} fill="var(--color-error-icon)" />
			{errorMessage}
		</span>
	) : undefined;

	switch (type) {
		case INPUT_TYPES.TEXTAREA:
			return (
				<TextArea
					id={name}
					name={name}
					value={(value as string) ?? ''}
					onChange={onChange}
					onBlur={onBlur}
					required={required}
					placeholder={secondaryLabel ?? label ?? ''}
					rows={3}
					wrapperClassName="pb-0"
					className="border-border-default resize-none overflow-y-auto max-h-[160px]"
					errorMessage={decoratedError}
				/>
			);

		case INPUT_TYPES.EMAIL:
			return (
				<Input
					type="email"
					id={name}
					name={name}
					label={label}
					value={(value as string) ?? ''}
					onChange={onChange}
					onBlur={onBlur}
					required={required}
					wrapperClassName="pb-0"
					className="border-border-default"
					errorMessage={decoratedError}
				/>
			);

		case INPUT_TYPES.DROPDOWN:
			return (
				<Select
					id={name}
					name={name}
					label={label}
					value={(value as string) ?? ''}
					onChange={onChange}
					onBlur={onBlur}
					required={required}
					fullWidth
					wrapperClassName="pb-0"
					options={(options || []).map(option => ({
						label: option.label ?? '',
						value: option.value ?? '',
						disabled: option.disabled,
					}))}
					errorMessage={decoratedError}
				/>
			);

		case INPUT_TYPES.RADIO:
		case INPUT_TYPES.BUTTON_OPTIONS:
			return (
				<div id={name} className="flex flex-col gap-150">
					{label && (
						<div className="flex items-baseline gap-040">
							<span className="text-label uppercase text-text-primary">
								<BevSafeText>{label}</BevSafeText>
							</span>
							{required && (
								<span className="text-error-text" aria-hidden="true">
									*
								</span>
							)}
						</div>
					)}
					<div className="grid grid-cols-3 gap-100" role="radiogroup" aria-label={label}>
						{(options || []).slice(0, 6).map(option => {
							const isSelected = value === option.value;
							return (
								<button
									key={String(option.value)}
									type="button"
									disabled={option.disabled}
									aria-pressed={isSelected}
									onClick={() =>
										onChange({
											target: { name, value: option.value },
										} as unknown as React.ChangeEvent<HTMLInputElement>)
									}
									className={cn(
										'text-button2 rounded-sm border px-200 py-100 text-center transition-colors',
										isSelected
											? 'border-border-strong bg-neutral-100 text-text-heading'
											: 'border-border-subtle bg-surface-default text-text-primary hover:bg-neutral-100',
										option.disabled && 'cursor-not-allowed opacity-40'
									)}
								>
									<BevSafeText>{option.label}</BevSafeText>
								</button>
							);
						})}
					</div>
					{decoratedError && (
						<span className="text-caption text-red-700" role="alert">
							{decoratedError}
						</span>
					)}
				</div>
			);

		case INPUT_TYPES.CHECKBOX:
			return (
				<Checkbox
					id={name}
					name={name}
					label={label}
					checked={Boolean(value)}
					onChange={checkedValue =>
						onChange({
							target: { name, value: checkedValue },
						} as unknown as React.ChangeEvent<HTMLInputElement>)
					}
					errorMessage={decoratedError}
				/>
			);

		case INPUT_TYPES.NUMBER: {
			const numericValue = typeof value === 'number' ? value : Number(value) || 0;
			const atMin = min !== undefined && numericValue <= min;
			const atMax = max !== undefined && numericValue >= max;
			return (
				<div className="flex items-center justify-between" data-testid={`quantity-field-${name}`}>
					<div className="flex flex-col gap-020">
						{label && (
							<span className="text-body2 text-text-primary">
								<BevSafeText>{label}</BevSafeText>
							</span>
						)}
						{secondaryLabel && (
							<span className="text-caption text-text-secondary">
								<BevSafeText>{secondaryLabel}</BevSafeText>
							</span>
						)}
					</div>
					<div className="flex items-center gap-150">
						<button
							type="button"
							aria-label={`Decrease ${label ?? name}`}
							disabled={atMin}
							onClick={() =>
								onChange({
									target: { name, value: numericValue - 1 },
								} as unknown as React.ChangeEvent<HTMLInputElement>)
							}
							className="flex items-center justify-center rounded-full text-text-heading disabled:opacity-30"
						>
							<ItemRemove width={30} height={30} />
						</button>
						<span className="text-body2 w-150 text-center text-text-primary">{numericValue}</span>
						<button
							type="button"
							aria-label={`Increase ${label ?? name}`}
							disabled={atMax}
							onClick={() =>
								onChange({
									target: { name, value: numericValue + 1 },
								} as unknown as React.ChangeEvent<HTMLInputElement>)
							}
							className="flex items-center justify-center rounded-full text-text-heading disabled:opacity-30"
						>
							<ItemAdd width={30} height={30} />
						</button>
					</div>
				</div>
			);
		}

		default:
			return (
				<Input
					type="text"
					id={name}
					name={name}
					label={label}
					value={(value as string) ?? ''}
					onChange={onChange}
					onBlur={onBlur}
					required={required}
					wrapperClassName="pb-0"
					errorMessage={decoratedError}
				/>
			);
	}
}
